import { inject, injectable } from 'inversify';
import { EGlobalDIKeys } from 'core/di/keys';
import type { IChannels } from '../Channels';
import { WS_MESSAGE_TYPE } from '../../../websockets';
import type { IRPCMessage, IRPCMessages, TRPCMessageFactory } from './types';

@injectable()
class RPCMessages implements IRPCMessages {
  constructor(
    @inject(EGlobalDIKeys.RPCMessageFactory)
    private readonly _factory: TRPCMessageFactory,
    @inject(EGlobalDIKeys.Channels) private readonly _channels: IChannels,
  ) {}

  exportProposal<T>(meta?: T): IRPCMessage<T> {
    return this._factory<T>(
      this._channels.filesChannel(),
      WS_MESSAGE_TYPE.EXPORT_PROPOSAL,
      meta,
    );
  }

  createInfocard<T>(meta?: T): IRPCMessage<T> {
    return this._factory<T>(
      this._channels.filesChannel(),
      WS_MESSAGE_TYPE.CREATE_INFOCARD,
      meta,
    );
  }

  createProtocol<T>(meta?: T): IRPCMessage<T> {
    return this._factory<T>(
      this._channels.filesChannel(),
      WS_MESSAGE_TYPE.CREATE_PROTOCOL,
      meta,
    );
  }

  exportLot = <T>(meta?: T) => {
    return this._factory<T>(
      this._channels.filesChannel(),
      WS_MESSAGE_TYPE.EXPORT_LOT,
      meta,
    );
  };

  importLot = <T>(meta?: T) => {
    return this._factory<T>(
      this._channels.filesChannel(),
      WS_MESSAGE_TYPE.IMPORT_LOT,
      meta,
    );
  };

  generateLotInfocard = <T>(meta?: T) => {
    return this._factory<T>(
      this._channels.filesChannel(),
      WS_MESSAGE_TYPE.LOT_INFOCARD,
      meta,
    );
  };

  exportProposalInfocard = <T>(meta?: T) => {
    return this._factory<T>(
      this._channels.filesChannel(),
      WS_MESSAGE_TYPE.EXPORT_PROPOSAL_INFOCARD,
      meta,
    );
  };

  importProposal = <T>(meta?: T) => {
    return this._factory<T>(
      this._channels.filesChannel(),
      WS_MESSAGE_TYPE.PARSE_EXCEL_PROPOSAL,
      meta,
    );
  };

  exportTenderAdditionalCriteria = <T>(meta?: T) => {
    return this._factory<T>(
      this._channels.filesChannel(),
      WS_MESSAGE_TYPE.EXPORT_TENDER_ADDITIONAL_CRITERIA,
      meta,
    );
  };

  importTenderAdditionalCriteria = <T>(meta?: T) => {
    return this._factory<T>(
      this._channels.filesChannel(),
      WS_MESSAGE_TYPE.IMPORT_TENDER_ADDITIONAL_CRITERIA,
      meta,
    );
  };

  exportRatings = <T>(meta?: T) => {
    return this._factory<T>(
      this._channels.filesChannel(),
      WS_MESSAGE_TYPE.EXPORT_RATINGS,
      meta,
    );
  };

  exportSecurityFormationConclusion = <T>(meta?: T) => {
    return this._factory<T>(
      this._channels.filesChannel(),
      WS_MESSAGE_TYPE.EXPORT_SECURITY_FORMATION_CONCLUSION,
      meta,
    );
  };

  exportAccreditationReport = <T>(meta?: T) => {
    return this._factory<T>(
      this._channels.filesChannel(),
      WS_MESSAGE_TYPE.EXPORT_ACCREDITATION_REPORT,
      meta,
    );
  };

  tenderCounter = <T>(meta?: T) => {
    return this._factory<T>(
      this._channels.tenderRpcChannel(),
      WS_MESSAGE_TYPE.TENDER_COUNTER,
      meta,
    );
  };

  aclDecisions = <T>(meta?: T) => {
    return this._factory<T>(
      this._channels.tenderRpcChannel(),
      WS_MESSAGE_TYPE.ACL_DECISIONS,
      meta,
    );
  };

  companyKonturFocus = <T>(meta?: T) => {
    return this._factory<T>(
      this._channels.companyRpcChannel(),
      WS_MESSAGE_TYPE.KONTUR_FOCUS,
      meta,
    );
  };

  getBestPrices = <T>(meta?: T) => {
    return this._factory<T>(
      this._channels.companyRpcChannel(),
      WS_MESSAGE_TYPE.PRICE_GUIDE,
      meta,
    );
  };

  lotBestPrices = <T>(meta?: T) => {
    return this._factory<T>(
      this._channels.lotRpcChannel(),
      WS_MESSAGE_TYPE.PRICE_GUIDE,
      meta,
    );
  };
}

export default RPCMessages;
